import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Image as ImgIcon } from "lucide-react";
import { inventoryApi } from "../lib/shop-api";
import { money, dateShort } from "../lib/api";

const STATUS = ["pendiente", "pagado", "preparando", "listo", "enviado", "completado", "cancelado"];
const PAY = ["pendiente", "pagado"];

export function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    inventoryApi.listOrders(status).then(setOrders).finally(() => setLoading(false));
  }, [status]);

  return (
    <div>
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#C89B3C] mb-2">Ventas</p>
          <h1 className="text-4xl text-[#F5F0E6]" style={{ fontFamily: "'Playfair Display'", fontWeight: 700 }}>Pedidos</h1>
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm text-[#F5F0E6]" data-testid="orders-status-filter">
          <option value="">Todos los estados</option>
          {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-[#120F0D] border border-white/10 rounded-xl overflow-hidden">
        {loading ? <p className="p-6 text-[#F5F0E6]/60">Cargando…</p> :
          orders.length === 0 ? <p className="p-6 text-[#F5F0E6]/60">Sin pedidos.</p> : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-[#F5F0E6]/50 border-b border-white/10">
                <th className="p-3"># Orden</th>
                <th className="p-3">Cliente</th>
                <th className="p-3">Entrega</th>
                <th className="p-3">Estado</th>
                <th className="p-3">Pago</th>
                <th className="p-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody data-testid="orders-table-body">
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-3">
                    <Link to={`/admin/orders/${o.id}`} className="font-mono text-[#C89B3C] hover:text-[#E6BF5F]" data-testid={`order-link-${o.order_number}`}>{o.order_number}</Link>
                    <p className="text-xs text-[#F5F0E6]/50">{dateShort(o.created_at)}</p>
                  </td>
                  <td className="p-3 text-[#F5F0E6]/90"><p>{o.customer_name}</p><p className="text-xs text-[#F5F0E6]/50">{o.customer_email}</p></td>
                  <td className="p-3 text-[#F5F0E6]/80">{o.fulfillment === "pickup" ? "Pickup" : "Envío"}</td>
                  <td className="p-3 uppercase text-xs tracking-wider text-[#C89B3C]">{o.status}</td>
                  <td className="p-3 uppercase text-xs tracking-wider">
                    <span className={o.payment_status === "pagado" ? "text-green-400" : "text-amber-400"}>{o.payment_status}</span>
                  </td>
                  <td className="p-3 text-right font-bold text-[#F5F0E6]">{money(o.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function AdminOrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => inventoryApi.getOrder(id).then(setO);
  useEffect(() => { load(); }, [id]);

  const update = async (patch) => {
    setSaving(true);
    try {
      const upd = await inventoryApi.patchOrder(id, patch);
      setO(upd);
      toast.success("Actualizado");
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  if (!o) return <p className="text-[#F5F0E6]/60">Cargando…</p>;

  return (
    <div className="max-w-4xl">
      <Link to="/admin/orders" className="inline-flex items-center gap-1 text-sm text-[#F5F0E6]/60 hover:text-[#C89B3C] mb-4"><ChevronLeft size={16} /> Pedidos</Link>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#C89B3C] mb-2">Orden</p>
          <h1 className="text-4xl font-mono text-[#F5F0E6]" data-testid="order-number">{o.order_number}</h1>
          <p className="text-sm text-[#F5F0E6]/60 mt-1">{dateShort(o.created_at)}</p>
        </div>
        <div className="flex gap-2">
          <select value={o.status} onChange={(e) => update({ status: e.target.value })} className="bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm text-[#F5F0E6]" data-testid="order-status-select">
            {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={o.payment_status} onChange={(e) => update({ payment_status: e.target.value })} className="bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm text-[#F5F0E6]" data-testid="order-payment-select">
            {PAY.map((s) => <option key={s} value={s}>pago: {s}</option>)}
          </select>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_300px] gap-6">
        <section className="bg-[#120F0D] border border-white/10 rounded-xl p-5">
          <h2 className="text-lg text-[#F5F0E6] mb-3" style={{ fontFamily: "'Playfair Display'" }}>Artículos</h2>
          <ul className="divide-y divide-white/10" data-testid="order-items">
            {o.items.map((it, i) => (
              <li key={i} className="py-3 flex gap-3">
                <div className="w-14 h-14 bg-black/40 rounded overflow-hidden shrink-0">
                  {it.image ? <img src={it.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20"><ImgIcon size={20} /></div>}
                </div>
                <div className="flex-1">
                  <p className="text-[#F5F0E6]">{it.product_name}</p>
                  <p className="text-xs text-[#F5F0E6]/60">{it.size && `Talla ${it.size}`}{it.size && it.color && " · "}{it.color}{" · "}× {it.quantity}</p>
                </div>
                <p className="text-[#F5F0E6] font-bold">{money(it.line_total)}</p>
              </li>
            ))}
          </ul>
          <div className="border-t border-white/10 mt-3 pt-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-[#F5F0E6]/70">Subtotal</span><span>{money(o.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-[#F5F0E6]/70">Envío</span><span>{money(o.shipping)}</span></div>
            <div className="flex justify-between font-bold text-lg mt-2"><span>Total</span><span>{money(o.total)}</span></div>
          </div>
        </section>

        <aside className="space-y-4">
          <section className="bg-[#120F0D] border border-white/10 rounded-xl p-5">
            <h2 className="text-sm text-[#C89B3C] uppercase tracking-wider mb-2">Cliente</h2>
            <p className="text-[#F5F0E6]">{o.customer_name}</p>
            <p className="text-sm text-[#F5F0E6]/70">{o.customer_email}</p>
            {o.customer_phone && <p className="text-sm text-[#F5F0E6]/70">{o.customer_phone}</p>}
          </section>
          <section className="bg-[#120F0D] border border-white/10 rounded-xl p-5">
            <h2 className="text-sm text-[#C89B3C] uppercase tracking-wider mb-2">Entrega</h2>
            <p className="text-[#F5F0E6]">{o.fulfillment === "pickup" ? "Pickup en tienda" : "Envío a domicilio"}</p>
            {o.shipping_address && (
              <div className="text-sm text-[#F5F0E6]/70 mt-1">
                <p>{o.shipping_address.line1}</p>
                {o.shipping_address.line2 && <p>{o.shipping_address.line2}</p>}
                <p>{o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.zip}</p>
              </div>
            )}
          </section>
          {o.notes && (
            <section className="bg-[#120F0D] border border-white/10 rounded-xl p-5">
              <h2 className="text-sm text-[#C89B3C] uppercase tracking-wider mb-2">Notas</h2>
              <p className="text-sm text-[#F5F0E6]/80 whitespace-pre-line">{o.notes}</p>
            </section>
          )}
        </aside>
      </div>
      {saving && <p className="text-xs text-[#F5F0E6]/50 mt-2">Guardando…</p>}
    </div>
  );
}
